import useUrlState from '@ahooksjs/use-url-state';
import { ProFormInstance } from '@ant-design/pro-form';
import { useLocalStorageState } from 'ahooks';
import React, { useEffect, useMemo, useRef } from 'react';
import * as uuid from 'uuid';
import { useAppConfig } from '@/config/context';
import { getParamFromUrl } from '@/infrastructure/history';
import { PAGE_ID } from '@/infrastructure/reporter/datong/id';
import {
  CHARACTER_MATCH_ARRAY_OPTIONS,
  CHARACTER_MATCH_OPTIONS,
  NUMBER_MATCH_OPTIONS,
  VERSION_MATCH_OPTIONS,
} from '@/presentation/components/form/standards/types';
import { FilterEntry } from '@/presentation/components/form/types';
import { iconText } from '@/presentation/components/hint-text/hintText';
import { enumFilterTips, numberFilterTips, versionFilterTips } from '@/presentation/components/issue-list/components/tips';
import { TdsFilterType, TdsForm, TdsFormItem, TdsFormItemType, ITdsFormRef } from '@/presentation/components/tds-form';
import { SPAN_DEFAULT_EVENT_TIME_RANGE } from '@/presentation/components/tds-form/config';
import { TdsCustomFieldsValue } from '@/presentation/components/tds-form/tds-custom-fields';
import { getInitTimeRange } from '@/presentation/components/tds-form/tds-date-cascader';
import { COMMON_TIME_TYPE_OPTIONS, ISSUE_TIME_TYPE } from '@/presentation/components/tds-form/tds-date-cascader/config';
import {
  customFields2Filter, customFilter,
  dateCascaderConverter,
  dateCascaderTransformer,
} from '@/presentation/components/tds-form/utils';
import { useTdsForm } from '@/presentation/hooks/form';
import {
  CommonFieldKey,
  FIELD_LABEL,
  FieldKey,
  StandardCommonFieldKey,
  Zone,
} from '@/proto/field';
import { IssueType } from '@/proto/issue';
import { IFilter } from '@/proto/metric';
import { genStandardToken, getTagParams, KeyDataType, parseStandardToken } from '@/proto/token';


export const CpuListForm = (props: {
  entry: FilterEntry;
  formRef: React.MutableRefObject<ProFormInstance<any> | undefined>;
  subFormRef: React.MutableRefObject<ProFormInstance<any> | undefined>;
  tdsFormRef: React.RefObject<ITdsFormRef>;
  subTdsFormRef: React.RefObject<ITdsFormRef>;
  onTokenChange?: (token: string) => void;
}) => {
  const { subFormRef, subTdsFormRef, entry, onTokenChange } = props;
  const ref = useRef<ProFormInstance>();
  const tdsRef = useRef<ITdsFormRef>(null);
  // 详情页不会传入Ref
  const formRef = props.formRef ?? ref;
  const tdsFormRef = props.tdsFormRef ?? tdsRef;
  const config = useAppConfig();
  console.log(entry);
  const [state, setState] = useUrlState({
    token: getParamFromUrl('token') || '',
  });
  const [timeRange, setTimeRange] = useLocalStorageState(
    'cpu-list-time-rangev3',
    {
      defaultValue: SPAN_DEFAULT_EVENT_TIME_RANGE,
    },
  );
  const [, useTdsFormContext] = useTdsForm;

  const { updateFormContext } = useTdsFormContext();

  const items: TdsFormItem[] = [
    {
      type: TdsFormItemType.DateCascader,
      label: '时间范围',
      name: StandardCommonFieldKey.TimeRange,
      width: 556,
      deleteable: false,
      convertValue: dateCascaderConverter,
      transform: dateCascaderTransformer,
      fieldProps: {
        maxLimitedDays: 90,
        timeWindowOptions: ISSUE_TIME_TYPE,
        timeTypeOptions: COMMON_TIME_TYPE_OPTIONS,
      },
      inFilter: true,
      filterType: TdsFilterType.DateCascader,
    },
    {
      type: TdsFormItemType.CharacterInput,
      labelTools: [iconText(versionFilterTips)],
      label: FIELD_LABEL[FieldKey.AppVersion],
      labelString: 'APP版本',
      name: StandardCommonFieldKey.AppVersion,
      deleteable: false,
      inFilter: true,
      filterType: TdsFilterType.Version,
      filterName: CommonFieldKey.AppVersion,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.AppVersion,
          fieldScope: Zone.HighCpuStack,
        },

        modeOptions: VERSION_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.FieldSelector,
      name: StandardCommonFieldKey.AppVersionMode,
      inFilter: true,
      labelString: '版本类型',
      labelTools: [iconText(enumFilterTips)],
      label: '版本类型',
      fieldProps: {
        mode: 'multiple',
        fieldKey: CommonFieldKey.AppVersionMode,
        fieldScope: Zone.HighCpuStack,
      },
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => customFilter(CommonFieldKey.AppVersionMode, { values: fieldValue }),
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '用户ID',
      name: StandardCommonFieldKey.AccountId,
      filterName: CommonFieldKey.AccountId,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '设备ID',
      name: StandardCommonFieldKey.Qimei,
      filterName: CommonFieldKey.Qimei,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: 'Bundle ID',
      name: StandardCommonFieldKey.BundleId,
      filterName: CommonFieldKey.BundleId,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.BundleId,
          fieldScope: Zone.HighCpuStack,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '监控线程',
      name: StandardCommonFieldKey.MonitoredThreadName,
      filterName: CommonFieldKey.MonitoredThreadName,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.MonitoredThreadName,
          fieldScope: Zone.HighCpuStack,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.FieldSelector,
      label: '系统堆栈',
      name: StandardCommonFieldKey.SystemStack,
      filterName: CommonFieldKey.SystemStack,
      inFilter: true,
      filterType: TdsFilterType.EnumString,
      fieldProps: {
        fieldKey: CommonFieldKey.SystemStack,
        fieldScope: Zone.HighCpuStack,
        mode: 'multiple',
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '实验ID',
      hidden: !config.enableShiplyAbTestTag,
      name: StandardCommonFieldKey.ShiplyTag,
      filterName: CommonFieldKey.ShiplyTag,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => customFields2Filter(CommonFieldKey.ShiplyAbTestTag, fieldValue, 'a11'),
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.ShiplyTag,
          fieldScope: Zone.HighCpuStack,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '业务下钻',
      name: StandardCommonFieldKey.AbTestTag,
      filterName: CommonFieldKey.ShiplyAbTestTag,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => customFields2Filter(CommonFieldKey.ShiplyAbTestTag, fieldValue, 'a12'),
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.AbTestTag,
          fieldScope: Zone.HighCpuStack,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '个例标签',
      name: StandardCommonFieldKey.TagsInFilter,
      filterName: CommonFieldKey.TagsInFilter,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => customFields2Filter(CommonFieldKey.TagsInFilter, fieldValue, 'a13'),
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.TagsInFilter,
          fieldScope: Zone.HighCpuStack,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: 'SDK版本',
      name: StandardCommonFieldKey.SdkVersion,
      filterName: CommonFieldKey.SdkVersion,
      inFilter: true,
      filterType: TdsFilterType.Version,
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.SdkVersion,
          fieldScope: Zone.HighCpuStack,
        },
        mode: 'tags',
        modeOptions: VERSION_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '机型',
      name: StandardCommonFieldKey.Model,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.Model,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Model,
          fieldScope: Zone.HighCpuStack,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '系统版本',
      name: StandardCommonFieldKey.OsVersion,
      filterType: TdsFilterType.Version,
      filterName: CommonFieldKey.OsVersion,
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.OsVersion,
          fieldScope: Zone.HighCpuStack,
        },
        mode: 'tags',
        modeOptions: VERSION_MATCH_OPTIONS,
      },
      inFilter: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '进程',
      name: StandardCommonFieldKey.ProcessName,
      filterName: CommonFieldKey.ProcessName,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '消息ID',
      name: StandardCommonFieldKey.ClientIdentify,
      filterName: CommonFieldKey.ClientIdentify,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: 'Issue ID',
      name: StandardCommonFieldKey.Feature,
      filterName: CommonFieldKey.Feature,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '进程启动ID',
      name: StandardCommonFieldKey.ProcessLaunchId,
      filterName: CommonFieldKey.ProcessLaunchId,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '问题特征',
      name: StandardCommonFieldKey.FuncName,
      filterName: CommonFieldKey.FuncName,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.NumberInput,
      label: '使用时长（秒）',
      labelString: '使用时长（秒）',
      labelTools: [iconText(numberFilterTips)],
      name: StandardCommonFieldKey.StandardLaunchDuration,
      fieldProps: {
        modeOptions: NUMBER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.LaunchDuration,
      filterType: TdsFilterType.Number,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '场景名称',
      name: StandardCommonFieldKey.Stage,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Stage,
          fieldScope: Zone.HighCpuStack,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.Stage,
      filterType: TdsFilterType.String,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: 'CPU型号',
      name: StandardCommonFieldKey.CpuArchitecture,
      filterName: CommonFieldKey.CpuArchitecture,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.CpuArchitecture,
          fieldScope: Zone.HighCpuStack,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      label: 'CPU核心数',
      type: TdsFormItemType.CharacterInput,
      name: StandardCommonFieldKey.CpuCores,
      filterName: CommonFieldKey.CpuCores,
      inFilter: true,
      filterType: TdsFilterType.Number,
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.CpuCores,
          fieldScope: Zone.HighCpuStack,
        },
        mode: 'single',
        modeOptions: NUMBER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.NumberInput,
      labelTools: [iconText(numberFilterTips)],
      label: '进程CPU使用率（%）',
      labelString: '进程CPU使用率（%）',
      name: StandardCommonFieldKey.AppCpuUsage,
      fieldProps: {
        modeOptions: NUMBER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.AppCpuUsage,
      filterType: TdsFilterType.Number,
    },
    {
      type: TdsFormItemType.NumberInput,
      labelTools: [iconText(numberFilterTips)],
      label: '系统CPU使用率（%）',
      labelString: '系统CPU使用率（%）',
      name: StandardCommonFieldKey.SysCpuUsage,
      fieldProps: {
        modeOptions: NUMBER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.SysCpuUsage,
      filterType: TdsFilterType.Number,
    },
    {
      type: TdsFormItemType.CustomPanel,
      label: '自定义维度',
      name: StandardCommonFieldKey.UserCustom,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterName: CommonFieldKey.UserCustom,
      fieldProps: {
        issueType: IssueType.ISSUE_TYPE_MEMORY_DETAIL,
        type: 'customFields',
      },
      filterTransform: (
        _fieldKey: string,
        fieldValue: TdsCustomFieldsValue[],
      ) => fieldValue?.map((item: any) => ({
        name: CommonFieldKey.UserCustom,
        operator: item.operator,
        values: item.values || [],
        key: item.key,
        type: KeyDataType.STRING,
      })) ?? [],
    },
    {
      type: TdsFormItemType.CustomPanel,
      label: '自定义字段',
      name: StandardCommonFieldKey.BizExtendInfo,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterName: CommonFieldKey.BizExtendInfo,
      fieldProps: {
        issueType: IssueType.ISSUE_TYPE_MEMORY_DETAIL,
        type: 'customKey',
      },
      filterTransform: (
        _fieldKey: string,
        fieldValue: TdsCustomFieldsValue[],
      ) => fieldValue?.map((item: any) => ({
        name: CommonFieldKey.BizExtendInfo,
        operator: item.operator,
        values: item.values || [],
        key: item.key,
        type: KeyDataType.STRING,
      })) ?? [],
    },
  ];

  const initToken = async () => {
    if (!state.token) {
      formRef.current?.submit?.();
    } else {
      const fieldsValue = await parseStandardToken(state.token);
      // 链接有token时，强制展示有value值的表单项
      tdsFormRef?.current?.showFormItem?.(items, fieldsValue)?.then?.(() => {
        formRef?.current?.setFieldsValue?.(fieldsValue);
        entry === FilterEntry.CpuList && subFormRef?.current?.setFieldsValue(fieldsValue);
      });
      updateFormContext(draft => ({
        ...draft,
        token: state.token,
        fieldsValue,
        fieldsFilter: fieldsValue?.filters ?? [],
        uuidFetch: uuid.v4(),
      }));
      onTokenChange?.(state.token);
    }
  };

  const onFinish = async (values: Record<string, any>, filters: IFilter[]) => {
    // 需要带上表格下方的筛选项
    const tableValues = subFormRef?.current?.getFieldsValue?.();
    setTimeRange(values?.[StandardCommonFieldKey.TimeRange] ?? SPAN_DEFAULT_EVENT_TIME_RANGE);
    const concatFilters = (filters || [])?.concat(subTdsFormRef?.current?.getFilter?.() || []);
    const token = await genStandardToken({
      ...values,
      ...tableValues,
      issue_status: tableValues?.standard_issue_status?.select_value,
      tapd_status: tableValues?.standard_tapd_status?.select_value,
      tapd_handler: tableValues?.standard_tapd_handler?.select_value,
      issue_status_operator: tableValues?.standard_issue_status?.switch_value ? 'NOT' : '',
      tapd_status_operator: tableValues?.standard_tapd_status?.switch_value ? 'NOT' : '',
      tapd_handler_operator: tableValues?.standard_tapd_handler?.switch_value ? 'NOT' : '',
      new_tag_list_operator: tableValues?.standard_tags_list?.switch_value ? 'NOT' : '',
      new_tag_list: tableValues?.standard_tags_list?.select_value?.length
        ? await getTagParams(tableValues?.standard_tags_list?.select_value, undefined) : [],
      tags: tableValues?.[CommonFieldKey.tagList]?.length
        ? await getTagParams(tableValues[CommonFieldKey.tagList]!, undefined) : undefined,
      time_window: values.time_window_type,
      filters: concatFilters,
    });

    updateFormContext(draft => ({
      ...draft,
      token,
      fieldsValue: values,
      fieldsFilter: concatFilters,
      uuidFetch: uuid.v4(),
    }));

    onTokenChange?.(state.token);
    setState({
      token,
    });
  };

  useEffect(() => {
    initToken();
  }, []);

  const initialTimeRange = useMemo(() => (timeRange ? {
    ...timeRange,
    date_value: getInitTimeRange(timeRange.time_window_type!, timeRange.short_cut!),
  } : timeRange), [timeRange]);

  return <TdsForm
    ref={tdsFormRef}
    pageId={PAGE_ID.cpu.list}
    formRef={formRef}
    onFinish={onFinish}
    items={items}
    name={'cpu-list'}
    initialValues={{
      [StandardCommonFieldKey.TimeRange]: initialTimeRange,
    }}
  />;
};
